import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Badge } from 'react-bootstrap';
import WorkoutLogger from './WorkoutLogger';
import { workoutAPI } from '../services/workoutAPI';

const EditWorkoutModal = ({ workoutLog, onSave, onCancel, userId }) => {
  const [transformedData, setTransformedData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (workoutLog) {
      const transformed = transformWorkoutForEdit(workoutLog);
      setTransformedData(transformed);
    }
  }, [workoutLog]);

  // Transform DB format to WorkoutLogger format
  const transformWorkoutForEdit = (workout) => {
    const exercisesMap = new Map();
    const supersets = new Map();

    // Process exercises and group by superset
    if (workout.exercises) {
      workout.exercises.forEach((ex, index) => {
        if (ex.item_type === 'rest') {
          // Rest block
          exercisesMap.set(ex.id, {
            type: 'rest',
            id: ex.id,
            rest_duration_seconds: ex.rest_duration_seconds,
            rest_notes: ex.rest_notes || '',
            sort_order: ex.sort_order !== undefined ? ex.sort_order : index,
            item_type: 'rest'
          });
        } else if (ex.superset_id) {
          // Part of a superset
          if (!supersets.has(ex.superset_id)) {
            supersets.set(ex.superset_id, {
              type: 'superset',
              id: ex.superset_id,
              exercises: [],
              sort_order: ex.sort_order !== undefined ? ex.sort_order : index
            });
          }

          const exerciseData = transformExercise(ex);
          supersets.get(ex.superset_id).exercises.push({
            ...exerciseData,
            superset_order: ex.superset_order
          });
        } else {
          // Regular exercise
          exercisesMap.set(ex.id, {
            type: 'exercise',
            ...transformExercise(ex),
            sort_order: ex.sort_order !== undefined ? ex.sort_order : index
          });
        }
      });
    }

    // Add supersets to exercisesMap
    supersets.forEach((superset) => {
      // Sort exercises within superset by superset_order
      superset.exercises.sort((a, b) => (a.superset_order || 0) - (b.superset_order || 0));
      exercisesMap.set(superset.id, superset);
    });

    // Convert map to array and sort by sort_order
    const exercisesArray = Array.from(exercisesMap.values()).sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    );

    // Extract just the date part (yyyy-MM-dd) from the database timestamp
    let formattedDate = new Date().toISOString().split('T')[0];
    if (workout.workout_date) {
      // Handle both "yyyy-MM-dd" and full ISO timestamp formats
      if (workout.workout_date.includes('T')) {
        formattedDate = workout.workout_date.split('T')[0];
      } else {
        formattedDate = workout.workout_date;
      }
    }

    return {
      workoutId: workout.id,
      workoutName: workout.name || '',
      workoutDate: formattedDate,
      workoutTime: workout.workout_time || '',
      workoutNotes: workout.notes || '',
      durationMinutes: workout.duration_minutes || 0,
      exercises: exercisesArray
    };
  };

  const transformExercise = (ex) => {
    const exercise = {
      id: ex.id,
      exercise_id: ex.exercise_id,
      exercise_name: ex.name,
      exercise_type: ex.exercise_type,
      category: ex.category,
      notes: ex.notes || '',
      item_type: ex.item_type || 'exercise'
    };

    // Add sets for weight exercises
    if (ex.exercise_type === 'weight_reps' || ex.exercise_type === 'weight_time') {
      exercise.sets = ex.sets || [];
    }

    // Add cardio data for basic cardio
    if (ex.exercise_type === 'cardio_basic' && ex.cardio) {
      exercise.cardio_data = {
        distance: ex.cardio.distance || '',
        distance_unit: ex.cardio.distance_unit || 'miles',
        duration_seconds: ex.cardio.duration_seconds || 0,
        heart_rate_avg: ex.cardio.heart_rate_avg || '',
        heart_rate_max: ex.cardio.heart_rate_max || '',
        notes: ex.cardio.notes || ''
      };
    }

    // Add segments for cardio segments
    if (ex.exercise_type === 'cardio_segments' && ex.segments) {
      exercise.cardio_segments = ex.segments || [];
    }

    return exercise;
  };

  const handleSaveWorkout = async (workoutData) => {
    try {
      setError(null);

      // Flatten the data structure for API
      const flatExercises = [];
      let sortOrder = 0;

      workoutData.exercises.forEach((item) => {
        if (item.type === 'rest') {
          flatExercises.push({
            item_type: 'rest',
            rest_duration_seconds: item.rest_duration_seconds,
            rest_notes: item.rest_notes,
            sort_order: sortOrder++
          });
        } else if (item.type === 'superset') {
          const supersetId = `superset_${Date.now()}_${sortOrder}`;
          item.exercises.forEach((ex, index) => {
            flatExercises.push({
              exercise_id: ex.exercise_id,
              notes: ex.notes,
              sets: ex.sets,
              cardio_data: ex.cardio_data,
              cardio_segments: ex.cardio_segments,
              superset_id: supersetId,
              superset_order: index,
              sort_order: sortOrder,
              item_type: 'exercise'
            });
          });
          sortOrder++;
        } else {
          flatExercises.push({
            exercise_id: item.exercise_id,
            notes: item.notes,
            sets: item.sets,
            cardio_data: item.cardio_data,
            cardio_segments: item.cardio_segments,
            superset_id: null,
            superset_order: null,
            sort_order: sortOrder++,
            item_type: 'exercise'
          });
        }
      });

      const updateData = {
        name: workoutData.workoutName,
        workout_date: workoutData.workoutDate,
        workout_time: workoutData.workoutTime || null,
        duration_minutes: workoutData.durationMinutes,
        notes: workoutData.workoutNotes,
        exercises: flatExercises
      };

      await workoutAPI.updateWorkoutLog(workoutLog.id, updateData);
      onSave();
    } catch (err) {
      console.error('Error updating workout:', err);
      setError('Failed to update workout. Please try again.');
    }
  };

  if (!transformedData) {
    return null;
  }

  return (
    <Modal show={true} onHide={onCancel} size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>
          Edit Workout
          {workoutLog.program_workout_id && (
            <Badge bg="success" className="ms-2">From Program</Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {workoutLog.program_workout_id && (
          <Alert variant="info">
            <i className="fas fa-info-circle me-2"></i>
            This workout is part of a program. You can edit it freely, but the program linkage will remain.
          </Alert>
        )}

        <WorkoutLogger
          initialData={transformedData}
          onSaveWorkout={handleSaveWorkout}
          isEditMode={true}
          userId={userId}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditWorkoutModal;
